import React, { useEffect, useState, useRef } from "react";
import { fetchFullCandidateReport } from "../../services/user/api";
import { getS3key } from "../../services/employee/api";
import axiosInstance from "../../services/axiosInstance";
import logo from "../../assets/images/Trueface.png";
import html2pdf from "html2pdf.js";
import PDFview from "./PDFView";
import { useNavigate } from "react-router-dom";
import {
  Download,
  ArrowLeft,
  ExternalLink,
  FileText,
  ImageIcon,
  User,
  FileCheck,
  MapPin,
  GraduationCap,
  Briefcase,
  ShieldAlert,
  FolderOpen
} from "lucide-react";

/* Dynamically load PDF.js from CDN */
const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

// DOCUMENT PREVIEW COMPONENT
const ReportDocPreview = ({ fileUrl, fileName }) => {
  const isPdf =
    fileName?.toLowerCase().endsWith(".pdf") ||
    fileUrl?.toLowerCase().includes(".pdf");

  const [pdfImageSrc, setPdfImageSrc] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(isPdf);

  useEffect(() => {
    let isMounted = true;

    if (isPdf && fileUrl) {
      setLoadingPdf(true);

      loadPdfJs()
        .then((pdfjs) => pdfjs.getDocument(fileUrl).promise)
        .then((pdf) => pdf.getPage(1))
        .then((page) => {
          if (!isMounted) return;

          const unscaledViewport = page.getViewport({ scale: 1.0 });
          const targetScale = Math.max(1.5, 1400 / unscaledViewport.width);
          const viewport = page.getViewport({ scale: targetScale });

          const tempCanvas = document.createElement("canvas");
          const context = tempCanvas.getContext("2d");
          tempCanvas.height = viewport.height;
          tempCanvas.width = viewport.width;

          return page
            .render({
              canvasContext: context,
              viewport: viewport
            })
            .promise.then(() => {
              if (isMounted) {
                const imgData = tempCanvas.toDataURL("image/jpeg", 0.95);
                setPdfImageSrc(imgData);
                setLoadingPdf(false);
              }
            });
        })
        .catch((err) => {
          console.error("PDF preview error:", err);
          if (isMounted) {
            setLoadingPdf(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [fileUrl, isPdf]);

  const displayImageSrc = isPdf ? pdfImageSrc : fileUrl;

  return (
    <div
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: "6px",
        padding: "8px",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        marginBottom: "10px"
      }}
    >
      <div
        className="pdf-avoid-break"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #f1f5f9",
          paddingBottom: "4px",
          width: "100%",
          boxSizing: "border-box"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
          {isPdf ? (
            <FileText size={15} color="#dc2626" style={{ flexShrink: 0 }} />
          ) : (
            <ImageIcon size={15} color="#2563eb" style={{ flexShrink: 0 }} />
          )}
          <span
            style={{
              fontSize: "10.5px",
              fontWeight: "700",
              color: "#1e293b",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {fileName || "Verification_Proof_Document"}
          </span>
        </div>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "10.5px",
            color: "#2563eb",
            fontWeight: "700",
            textDecoration: "none",
            flexShrink: 0
          }}
        >
          <ExternalLink size={12} /> Open Original
        </a>
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          background: "#f8fafc",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "4px",
          boxSizing: "border-box"
        }}
      >
        {isPdf && loadingPdf && (
          <div style={{ padding: "16px", fontSize: "11px", color: "#64748b" }}>
            Rendering PDF Document...
          </div>
        )}

        {displayImageSrc && (
          <img
            src={displayImageSrc}
            alt={fileName || "Attachment Preview"}
            style={{
              width: "100%",
              maxWidth: "100%",
              height: "auto",
              maxHeight: "520px",
              display: "block",
              objectFit: "contain",
              margin: "0 auto",
              borderRadius: "3px",
              boxSizing: "border-box"
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        )}
      </div>
    </div>
  );
};

// STATUS BADGE COMPONENT (Robust mapping)
const StatusBadge = ({ status }) => {
  if (status === null || status === undefined || status === "") {
    status = "Verified";
  }

  if (status === true || status === "true" || status === "TRUE" || status === 1) {
    status = "Verified";
  } else if (status === false || status === "false" || status === "FALSE" || status === 0) {
    status = "Flagged";
  }

  const normalized = String(status || "").trim().toLowerCase().replace(/[_\s-]/g, "");
  
  let bg = "#e8f5e9";
  let color = "#2e7d32";
  let border = "#c8e6c9";
  let text = "✓ Verified";

  if (
    normalized.includes("clear") ||
    normalized.includes("verified") ||
    normalized.includes("pass") ||
    normalized.includes("approved") ||
    normalized.includes("completed")
  ) {
    bg = "#e8f5e9";
    color = "#2e7d32";
    border = "#c8e6c9";
    text = "✓ Verified";
  } else if (
    normalized.includes("review") ||
    normalized.includes("disposed") ||
    normalized.includes("closed") ||
    normalized.includes("pending") ||
    normalized.includes("inprogress") ||
    normalized.includes("workinprogress")
  ) {
    bg = "#fefce8";
    color = "#ca8a04";
    border = "#fde047";
    text = String(status).toUpperCase().replace(/_/g, " ");
  } else if (
    normalized.includes("flag") ||
    normalized.includes("fail") ||
    normalized.includes("reject") ||
    normalized.includes("risk")
  ) {
    bg = "#fef2f2";
    color = "#dc2626";
    border = "#fca5a5";
    text = "⚠ Flagged";
  } else {
    text = String(status).toUpperCase().replace(/_/g, " ");
  }

  return (
    <span
      style={{
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        padding: "2px 8px",
        borderRadius: "4px",
        fontWeight: "700",
        fontSize: "10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        lineHeight: "1.2"
      }}
    >
      {text}
    </span>
  );
};

  //  SINGLE CONTAINER FINAL REPORT PAGE
const FinalReportPage = ({ candidateId, onBack }) => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [currentAddressProofs, setCurrentAddressProofs] = useState([]);
  const [permanentAddressProofs, setPermanentAddressProofs] = useState([]);
  const [educationProofs, setEducationProofs] = useState({});
  const [employmentPhysicalProofs, setEmploymentPhysicalProofs] = useState({});
  const [employmentVirtualProofs, setEmploymentVirtualProofs] = useState({});

  const { employeeVerificationInfo, adminVerificationResult } = reportData || {};
  const {
    personalCheck,
    addressCheck,
    educationCheck: rawEduCheck,
    educationChecks,
    candidateEducation,
    employmentCheck: rawEmploymentCheck,
    employmentChecks,
    employmentCheckRequests,
    employmentDetails,
    employmentList,
    workExperience,
    attachmentResponses,
    criminalCheckRequests,
    criminalCheckResponse,
    candidateCriminalDetails,
    cases: rawCases,
    employmentUANCheckRequests,
    uanCheck
  } = employeeVerificationInfo || {};

  // 1. UNIFY EDUCATION LIST (Candidate DB + Employee DB fallback)
  const rawEduList =
    rawEduCheck ||
    educationChecks ||
    candidateEducation ||
    reportData?.educationCheck ||
    reportData?.candidateEducation ||
    [];

  const educationCheck = Array.isArray(rawEduList) ? rawEduList : [];

  // 2. UNIFY EMPLOYMENT LIST & DETECT MODE
  const primaryEmpList =
    rawEmploymentCheck ||
    employmentChecks ||
    employmentCheckRequests ||
    employmentDetails ||
    employmentList ||
    workExperience ||
    reportData?.employmentCheck ||
    reportData?.employmentChecks ||
    [];

  let combinedEmployment = Array.isArray(primaryEmpList) ? [...primaryEmpList] : [];

  if (
    combinedEmployment.length === 0 &&
    Array.isArray(attachmentResponses) &&
    attachmentResponses.length > 0
  ) {
    combinedEmployment = attachmentResponses.map((att) => ({
      employeeCode: att.employeeCode || att.empCode || "N/A",
      companyName: att.companyName || att.company || "N/A",
      jobTitle: att.jobTitle || att.designation || att.role || "N/A",
      location: att.location || att.city || "N/A",
      hrContactEmail: att.hrContactEmail || att.hrEmail || att.email || "",
      hrContactNumber: att.hrContactNumber || att.hrPhone || att.mobileNumber || "",
      workingFrom: att.workingFrom || att.durationFrom || att.fromDate || att.startDate || "N/A",
      workingTo: att.workingTo || att.durationTo || att.toDate || att.endDate || "N/A",
      remarks: att.remarks || "Employment details submitted",
      individualStatus: att.isVerified || att.status || "COMPLETED",
      employmentFileUrl: att.fileUrl || att.documentUrl || "",
      checkMode: att.checkMode || "VIRTUAL"
    }));
  }

  const employmentCheck = combinedEmployment.map((emp) => {
    const fileUrl = emp.employmentFileUrl || emp.employmentProofFileName || emp.fileUrl || "";
    const isVirtual =
      (emp.checkMode && String(emp.checkMode).toUpperCase() === "VIRTUAL") ||
      (emp.check_mode && String(emp.check_mode).toUpperCase() === "VIRTUAL") ||
      fileUrl.includes("/virtual-employment/");

    return {
      ...emp,
      checkMode: isVirtual ? "VIRTUAL" : "PHYSICAL"
    };
  });

  // 3. ROBUST UAN PARSING & VERIFIED STATUS OVERRIDE
  const firstUanReq =
    Array.isArray(employmentUANCheckRequests) && employmentUANCheckRequests.length > 0
      ? employmentUANCheckRequests[0]
      : null;

  const rawUanObj = firstUanReq || uanCheck || reportData?.uanCheck || {};

  const foundUanNumber =
    rawUanObj.uanNumber ||
    rawUanObj.uan ||
    rawUanObj.pfUan ||
    rawUanObj.uanNo ||
    (Array.isArray(employmentCheck) && employmentCheck.find((e) => e?.uanNumber || e?.uan || e?.pfUan)?.uanNumber) ||
    (Array.isArray(employmentCheck) && employmentCheck.find((e) => e?.uanNumber || e?.uan || e?.pfUan)?.uan) ||
    "";

  const uanMemberIdValue =
    rawUanObj.memberId ||
    rawUanObj.pfNumber ||
    rawUanObj.pfMemberId ||
    rawUanObj.member_id ||
    "N/A";

  const uanCompanyNameValue =
    rawUanObj.companyName ||
    rawUanObj.establishmentName ||
    rawUanObj.company_name ||
    (Array.isArray(employmentCheck) && employmentCheck[0]?.companyName) ||
    "N/A";

  const rawStatusField =
    reportData?.uanCheckStatus ||
    reportData?.data?.uanCheckStatus ||
    reportData?.employeeVerificationInfo?.uanCheckStatus ||
    rawUanObj.uanStatus ||
    rawUanObj.verificationStatus ||
    rawUanObj.individualStatus ||
    rawUanObj.bgcStatus ||
    rawUanObj.status ||
    "";

  const isUanDone =
    String(reportData?.uanCheckStatus).toUpperCase() === "COMPLETED" ||
    String(rawStatusField).toUpperCase() === "COMPLETED" ||
    String(rawStatusField).toUpperCase() === "VERIFIED" ||
    String(rawStatusField).toUpperCase() === "CLEAR" ||
    (uanMemberIdValue && uanMemberIdValue !== "N/A" && uanMemberIdValue !== "") ||
    rawUanObj.verified === true ||
    rawUanObj.isVerified === true;

  const hasUanCheck = Boolean(
    foundUanNumber ||
      reportData?.uanCheckStatus ||
      (uanMemberIdValue && uanMemberIdValue !== "N/A") ||
      (Array.isArray(employmentUANCheckRequests) && employmentUANCheckRequests.length > 0)
  );

  const uanNumberValue = foundUanNumber || "N/A";
  const uanStatusValue = isUanDone ? "Verified" : (rawStatusField || "WORK_IN_PROGRESS");

  // 4. UNIFY CRIMINAL CHECK (Surepass/ecourts, cases[], and DB formats)
  const candidateCases = 
    rawCases ||
    criminalCheckResponse?.cases ||
    candidateCriminalDetails?.cases ||
    reportData?.criminalCheck?.cases ||
    reportData?.cases ||
    criminalCheckRequests ||
    [];

  const validCriminalRequests = Array.isArray(candidateCases)
    ? candidateCases.filter((c) => {
        if (!c) return false;
        return c.year || c.caseNumber || c.caseName || c.petitioner || c.respondent || c.caseNo;
      })
    : [];

  const fatherNameValue = personalCheck?.fatherName || personalCheck?.fathersName || "N/A";
  const mobileNumberValue = personalCheck?.mobileNumber || personalCheck?.mobile || "N/A";
  const personalStatusValue =
    (personalCheck?.verificationStatus && String(personalCheck.verificationStatus).trim()) ||
    (personalCheck?.status && String(personalCheck.status).trim()) ||
    "Verified";

  const hasEmployment = employmentCheck.length > 0;
  const criminalRecordCount = validCriminalRequests.length;

  const highRiskCount =
    criminalRecordCount > 0
      ? validCriminalRequests.filter(
          (c) =>
            c?.caseStatus &&
            c.caseStatus.toLowerCase() !== "disposed" &&
            c.caseStatus.toLowerCase() !== "closed"
        ).length
      : 0;

  const mediumRiskCount =
    criminalRecordCount > 0
      ? validCriminalRequests.filter(
          (c) =>
            c?.caseStatus &&
            (c.caseStatus.toLowerCase() === "disposed" ||
              c.caseStatus.toLowerCase() === "closed")
        ).length
      : 0;

  const totalCompletedChecks =
    (personalCheck ? 1 : 0) +
    (addressCheck ? 1 : 0) +
    (educationCheck && educationCheck.length > 0 ? 1 : 0) +
    (hasEmployment ? 1 : 0) +
    (hasUanCheck && isUanDone ? 1 : 0);

  const lowRiskCount = Math.max(0, totalCompletedChecks - (highRiskCount + mediumRiskCount));
  const dynamicOverallRisk = highRiskCount > 0 ? "High" : mediumRiskCount > 0 ? "Medium" : "Low";
  const dynamicOverallStatus = highRiskCount > 0 ? "FLAGGED" : mediumRiskCount > 0 ? "REVIEW" : "CLEAR";

  // Fetch report data with direct DB fallbacks for Education & Criminal checks
  useEffect(() => {
    setCurrentAddressProofs([]);
    setPermanentAddressProofs([]);
    setEducationProofs({});
    setEmploymentPhysicalProofs({});
    setEmploymentVirtualProofs({});

    fetchFullCandidateReport(candidateId)
      .then(async (res) => {
        if (res?.data?.status) {
          let fullData = res.data.data;
          let empInfo = fullData?.employeeVerificationInfo || {};

          // Fallback 1: Direct Employment API
          if (!empInfo?.employmentCheck || empInfo.employmentCheck.length === 0) {
            try {
              const directEmpRes = await axiosInstance.get(`/api/employee/get/employment/${candidateId}`);
              const directEmpData = directEmpRes?.data?.data;
              if (directEmpData && Array.isArray(directEmpData) && directEmpData.length > 0) {
                empInfo = { ...empInfo, employmentCheck: directEmpData };
              }
            } catch (err) {
              console.warn("Employment direct query skipped:", err);
            }
          }

          // Fallback 2: Direct Candidate Education API
          if (!empInfo?.educationCheck || empInfo.educationCheck.length === 0) {
            try {
              const directEduRes = await axiosInstance.get(`/api/candidate/education/${candidateId}`);
              const directEduData = directEduRes?.data?.data;
              if (directEduData && Array.isArray(directEduData) && directEduData.length > 0) {
                empInfo = {
                  ...empInfo,
                  educationCheck: directEduData.map((e) => ({
                    educationLevel: e.educationLevel || e.education_level || e.qualification,
                    instituteName: e.instituteName || e.institute_name,
                    enrolledYear: e.enrolledYear || e.enrolled_year,
                    passingYear: e.passingYear || e.passing_year,
                    verificationStatus: e.verificationStatus || e.education_status || "Verified",
                    remarks: e.remarks || "Academic details verified",
                    educationFileUrl: e.certificateFileUrl || e.certificate_file_url
                  }))
                };
              }
            } catch (eduErr) {
              console.warn("Education direct query skipped:", eduErr);
            }
          }

          // Fallback 3: Direct Criminal Records API
          if (!empInfo?.cases || empInfo.cases.length === 0) {
            try {
              const crimRes = await axiosInstance.get(`/api/employee/criminal-surpas-check/candidate/${candidateId}`);
              const crimData = crimRes?.data?.data;
              if (crimData && crimData.cases && crimData.cases.length > 0) {
                empInfo = {
                  ...empInfo,
                  cases: crimData.cases,
                  criminalCheckStatus: crimData.criminalCheckStatus
                };
              }
            } catch (crimErr) {
              console.warn("Criminal check direct query skipped:", crimErr);
            }
          }

          fullData = { ...fullData, employeeVerificationInfo: empInfo };
          setReportData(fullData);
        }
      })
      .catch((err) => console.error("Error fetching report data:", err));
  }, [candidateId]);

  // Load Proofs
  useEffect(() => {
    if (!reportData?.employeeVerificationInfo) return;
    const { addressCheck: addrChk, educationCheck: eduChk } = reportData.employeeVerificationInfo;
    const empChk = employmentCheck;
    const visitedUrls = new Set();

    const loadProof = async (proofPath) => {
      if (!proofPath || typeof proofPath !== "string" || visitedUrls.has(proofPath)) return null;
      visitedUrls.add(proofPath);

      try {
        let cleanKey = proofPath;
        if (proofPath.startsWith("http://") || proofPath.startsWith("https://")) {
          const urlParts = proofPath.split(".amazonaws.com/");
          if (urlParts.length > 1) cleanKey = urlParts[1];
        }
        cleanKey = cleanKey.split("?")[0];
        const fileName = cleanKey.split("/").pop() || "Document_Proof";

        const response = await getS3key(cleanKey);
        if (!response || !response.data) return null;

        const fileBlob = response.data instanceof Blob ? response.data : new Blob([response.data]);
        if (fileBlob.size === 0) return null;

        return { fileName, url: URL.createObjectURL(fileBlob) };
      } catch (error) {
        console.error("Error loading proof asset:", error);
        return null;
      }
    };

    // Address Proofs
    if (addrChk) {
      const currentUrl =
        addrChk.currentAddressVerificationUrl ||
        addrChk.currentAddressFileUrl ||
        addrChk.addressFileUrl;

      const permanentUrl =
        addrChk.permanentAddressVerificationUrl || addrChk.permanentAddressFileUrl;

      const currentFull = [
        addrChk.currentAddressLine1,
        addrChk.currentAddressLine2,
        addrChk.currentCity,
        addrChk.currentState,
        addrChk.currentCountry,
        addrChk.currentPincode || addrChk.currentZipCode
      ]
        .filter((part) => Boolean(part) && String(part).toLowerCase() !== "null")
        .join(", ");

      const permFull = [
        addrChk.permanentAddressLine1 || addrChk.permAddressLine1,
        addrChk.permanentAddressLine2 || addrChk.permAddressLine2,
        addrChk.permanentCity || addrChk.permCity,
        addrChk.permanentState || addrChk.permState,
        addrChk.permanentCountry || addrChk.permCountry,
        addrChk.permanentPincode || addrChk.permPincode || addrChk.permanentZipCode
      ]
        .filter((part) => Boolean(part) && String(part).toLowerCase() !== "null")
        .join(", ");

      const isSame =
        permFull.toLowerCase().replace(/[\s,]/g, "") === "" ||
        currentFull.toLowerCase().replace(/[\s,]/g, "") === permFull.toLowerCase().replace(/[\s,]/g, "") ||
        addrChk.isSameAsCurrent === true ||
        addrChk.isSameAddress === true;

      if (currentUrl) {
        loadProof(currentUrl).then((f) => f && setCurrentAddressProofs([f]));
      }

      if (!isSame && permanentUrl && permanentUrl !== currentUrl) {
        loadProof(permanentUrl).then((f) => f && setPermanentAddressProofs([f]));
      } else {
        setPermanentAddressProofs([]);
      }
    }

    // Education Proofs
    if (Array.isArray(eduChk) && eduChk.length > 0) {
      (async () => {
        const grouped = {};
        for (const edu of eduChk) {
          const fileUrl = edu.educationFileUrl || edu.fileUrl || edu.documentUrl || edu.attachmentUrl || edu.certificateFileUrl || edu.certificate_file_url;
          if (!fileUrl) continue;
          const proof = await loadProof(fileUrl);
          if (!proof) continue;

          const level = edu.educationLevel || edu.education_level || edu.qualification || "Qualification Proof";
          if (!grouped[level]) grouped[level] = [];
          grouped[level].push({ ...proof, instituteName: edu.instituteName || edu.institute_name || "N/A" });
        }
        setEducationProofs(grouped);
      })();
    }

    // Employment Proofs
    if (Array.isArray(empChk) && empChk.length > 0) {
      (async () => {
        const physicalGrouped = {};
        const virtualGrouped = {};

        for (const emp of empChk) {
          const fileUrl =
            emp.employmentFileUrl ||
            emp.fileUrl ||
            emp.documentUrl ||
            emp.employmentProofFileName ||
            emp.experienceCertificate;

          if (!fileUrl) continue;
          const proof = await loadProof(fileUrl);
          if (!proof) continue;

          const companyName = emp.companyName || "Unknown Company";
          const isVirtual = emp.checkMode === "VIRTUAL" || fileUrl.includes("/virtual-employment/");

          if (isVirtual) {
            if (!virtualGrouped[companyName]) virtualGrouped[companyName] = [];
            virtualGrouped[companyName].push({
              ...proof,
              employeeCode: emp.employeeCode || emp.empCode || "",
              verifierName: emp.verifierName || ""
            });
          } else {
            if (!physicalGrouped[companyName]) physicalGrouped[companyName] = [];
            physicalGrouped[companyName].push({ ...proof, companyName });
          }
        }

        setEmploymentPhysicalProofs(physicalGrouped);
        setEmploymentVirtualProofs(virtualGrouped);
      })();
    }
  }, [reportData]);

  // PDF Export
  const handleDownload = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const reportElement = document.getElementById("final-report-container");
    if (!reportElement) return;

    const printStyles = document.createElement("style");
    printStyles.innerHTML = `
      #final-report-container {
        width: 794px !important;
        margin: 0 auto !important;
        background: #ffffff !important;
      }
      .pdf-avoid-break {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .proof-page-block {
        page-break-before: always !important;
        break-before: page !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        margin-top: 10px !important;
      }
      .proof-page-block img {
        max-height: 520px !important;
        object-fit: contain !important;
      }
    `;
    document.head.appendChild(printStyles);
    reportElement.classList.add("pdf-export-mode");

    const loadImageAsBase64 = async (url) => {
      try {
        if (!url || url.startsWith("data:")) return url;
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) return null;
        const blob = await response.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    try {
      const images = Array.from(reportElement.querySelectorAll("img"));
      await Promise.all(
        images.map(async (img) => {
          const base64 = await loadImageAsBase64(img.src);
          if (base64) img.src = base64;
        })
      );

      await new Promise((r) => setTimeout(r, 250));

      // DEFINING OPT BEFORE PASSING TO html2pdf
      const opt = {
        margin: [8, 0, 8, 0],
        filename: `Candidate_Report_${candidateId}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff",
          scrollY: 0,
          scrollX: 0
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"], before: ".proof-page-block", avoid: ".pdf-avoid-break" }
      };

      // Direct capture from HTML to PDF
      await html2pdf().set(opt).from(reportElement).save();

    } catch (err) {
      console.error("PDF Download Failure:", err);
    } finally {
      reportElement.classList.remove("pdf-export-mode");
      if (printStyles.parentNode) {
        document.head.removeChild(printStyles);
      }
    }
  };

  if (!reportData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <h2>Loading Candidate Report...</h2>
      </div>
    );
  }

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 32px",
    backgroundColor: "#001144",
    color: "#ffffff"
  };

  const logoContainerStyle = {
    background: "#ffffff",
    padding: "4px 10px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px"
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0 32px",
    marginTop: "16px",
    marginBottom: "8px"
  };

  const tableHeaderStyle = {
    backgroundColor: "#001144",
    color: "#ffffff",
    padding: "6px 8px",
    textAlign: "left",
    fontSize: "10.5px",
    fontWeight: "600",
    fontFamily: "Arial, sans-serif",
    verticalAlign: "middle"
  };

  const cellStyle = {
    padding: "6px 8px",
    borderBottom: "1px solid #cbd5e1",
    fontSize: "10px",
    color: "#1a1a1a",
    fontFamily: "Arial, sans-serif",
    whiteSpace: "normal",
    wordBreak: "break-word",
    verticalAlign: "middle"
  };

  const labelCellStyle = {
    ...cellStyle,
    background: "#f8fafc",
    color: "#002266",
    fontWeight: "600",
    width: "120px",
    border: "1px solid #cbd5e1"
  };

  const valueCellStyle = { ...cellStyle, border: "1px solid #cbd5e1" };

  const pdfSectionNumStyle = {
    width: "18px",
    height: "18px",
    background: "#001144",
    color: "#ffffff",
    fontSize: "9.5px",
    fontWeight: "700",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  const pdfSectionTitleStyle = {
    fontSize: "11px",
    fontWeight: "700",
    color: "#001144",
    textTransform: "uppercase",
    fontFamily: "Arial, sans-serif"
  };

  const documentsLabelHeaderStyle = {
    fontSize: "11px",
    fontWeight: "700",
    color: "#001144",
    background: "#e2e8f0",
    padding: "6px 10px",
    borderRadius: "4px",
    marginTop: "10px",
    marginBottom: "8px",
    fontFamily: "Arial",
    borderLeft: "3px solid #001144"
  };

  const liveUiFooterStyle = {
    borderTop: "1px solid #cbd5e1",
    padding: "14px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "10px",
    color: "#495f7e",
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#ffffff",
    marginTop: "24px"
  };

  const hasAddressDocs = currentAddressProofs.length > 0 || permanentAddressProofs.length > 0;
  const hasEduDocs = Object.keys(educationProofs).length > 0;
  const hasEmpDocs = Object.keys(employmentPhysicalProofs).length > 0;
  const hasVirtDocs = Object.keys(employmentVirtualProofs).length > 0;
  const hasAnyDocuments = hasAddressDocs || hasEduDocs || hasEmpDocs || hasVirtDocs;

  let sectionCounter = 1;

  return (
    <div
      style={{
        padding: "16px",
        background: "#e8e8e8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}
    >
      {/* Action Bar */}
      <div
        style={{
          width: "794px",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "12px"
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 14px",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "12px"
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          onClick={handleDownload}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 16px",
            background: "#001144",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "12px"
          }}
        >
          <Download size={14} /> Download PDF Report
        </button>
      </div>

      {/* MASTER REPORT CONTAINER */}
      <div
        id="final-report-container"
        style={{
          width: "794px",
          background: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          boxSizing: "border-box",
          paddingBottom: "16px"
        }}
      >
        {/* Main Header */}
        <div style={headerStyle}>
          <div style={logoContainerStyle}>
            <img src={logo} alt="TrueFace" style={{ width: "28px", height: "32px", objectFit: "contain" }} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: "1" }}>
              <span style={{ fontSize: "18px", fontWeight: "800", color: "#052680" }}>
                True<span style={{ color: "#de8406" }}>Face</span>
              </span>
              <span style={{ fontSize: "6px", fontWeight: "700", color: "#092246" }}>
                Powered by Client Server Technology Solutions
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: "9.5px", color: "#eeeeee", lineHeight: "1.25", fontFamily: "Arial" }}>
            <strong>CLIENTSERVER SOLUTIONS PVT LTD</strong><br />
            Suite#805, 9th Floor, DSL Abacus IT Park, <br />
            IDA Uppal, Hyderabad, Telangana – 500039.<br />
            hrind@clientservertech.com, +91 9640009669.
          </div>
        </div>

        {/* Candidate Title & Overall Risk Badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 32px 6px 32px" }}>
          <div style={{ textAlign: "left", fontFamily: "Arial" }}>
            <h1 style={{ fontSize: "20px", fontWeight: "800", color: "#c4953c", margin: "0 0 2px 0" }}>
              {personalCheck?.firstName} {personalCheck?.lastName}
            </h1>
            <p style={{ fontSize: "11px", color: "#475569", fontWeight: "600", margin: 0 }}>
              Candidate ID: {personalCheck?.candidateId || "TRUXXXXX1"} |{" "}
              <strong style={{ color: "#001144" }}>{hasEmployment ? "Experienced Candidate" : "Fresher"}</strong>
            </p>
          </div>

          {/* Dynamic Overall Badge */}
          <div
            style={{
              border: `2px solid ${highRiskCount > 0 ? "#dc2626" : mediumRiskCount > 0 ? "#ca8a04" : "#16a34a"}`,
              padding: "4px 14px",
              borderRadius: "6px",
              background: highRiskCount > 0 ? "#fef2f2" : mediumRiskCount > 0 ? "#fefce8" : "#f0fdf4",
              textAlign: "center",
              minWidth: "100px"
            }}
          >
            <div
              style={{
                fontSize: "8.5px",
                fontWeight: "700",
                color: highRiskCount > 0 ? "#dc2626" : mediumRiskCount > 0 ? "#ca8a04" : "#16a34a",
                letterSpacing: "0.5px",
                textTransform: "uppercase"
              }}
            >
              Overall
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "900",
                color: highRiskCount > 0 ? "#991b1b" : mediumRiskCount > 0 ? "#854d0e" : "#15803d",
                lineHeight: "1"
              }}
            >
              {dynamicOverallStatus}
            </div>
            <div style={{ display: "flex", gap: "2px", justifyContent: "center", margin: "2px 0" }}>
              {[...Array(5)].map((_, i) => (
                <span key={i} style={{ color: "#f8be12", fontSize: "11px" }}>★</span>
              ))}
            </div>
            <div
              style={{
                fontSize: "9.5px",
                fontWeight: "700",
                color: highRiskCount > 0 ? "#dc2626" : mediumRiskCount > 0 ? "#ca8a04" : "#16a34a"
              }}
            >
              Risk: {dynamicOverallRisk}
            </div>
          </div>
        </div>

        {/* Section 1: Candidate Information */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <User size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Candidate Information</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>
          <div style={{ padding: "0 32px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
              <tbody>
                <tr>
                  <td style={labelCellStyle}>Full Name</td>
                  <td style={valueCellStyle}>{personalCheck?.firstName} {personalCheck?.lastName}</td>
                  <td style={labelCellStyle}>Candidate ID</td>
                  <td style={valueCellStyle}>{personalCheck?.candidateId}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Date Of Birth</td>
                  <td style={valueCellStyle}>{personalCheck?.dob || "N/A"}</td>
                  <td style={labelCellStyle}>Father's Name</td>
                  <td style={valueCellStyle}>{fatherNameValue}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Email</td>
                  <td style={valueCellStyle}>{personalCheck?.email}</td>
                  <td style={labelCellStyle}>Mobile</td>
                  <td style={valueCellStyle}>{mobileNumberValue}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Client</td>
                  <td style={valueCellStyle}>Client Server Technology Solutions Pvt Ltd</td>
                  <td style={labelCellStyle}>Report Status</td>
                  <td style={valueCellStyle}>
                    <StatusBadge status={adminVerificationResult?.verificationStatus || "CLEAR"} />
                  </td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Verifier Name</td>
                  <td style={valueCellStyle}>{adminVerificationResult?.verifierName || "Vikranth.g"}</td>
                  <td style={labelCellStyle}>Verifier Email</td>
                  <td style={valueCellStyle}>{adminVerificationResult?.verifierEmail || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 2: Verification Check Summary */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <FileCheck size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Verification Check Summary</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>
          <div style={{ padding: "0 32px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
              <thead>
                <tr>
                  <th style={{ ...tableHeaderStyle, width: "24%" }}>Check Type</th>
                  <th style={{ ...tableHeaderStyle, width: "16%", textAlign: "center" }}>Status</th>
                  <th style={{ ...tableHeaderStyle, width: "30%" }}>Verified Value</th>
                  <th style={{ ...tableHeaderStyle, width: "30%" }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={cellStyle}>Personal Verification</td>
                  <td style={{ ...cellStyle, textAlign: "center" }}><StatusBadge status={personalStatusValue} /></td>
                  <td style={cellStyle}>Name, DOB, Family Summary</td>
                  <td style={cellStyle}>{personalCheck?.remarks || "Personal details verified"}</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Address Verification</td>
                  <td style={{ ...cellStyle, textAlign: "center" }}><StatusBadge status={addressCheck ? "Verified" : "N/A"} /></td>
                  <td style={cellStyle}>Address Details Location Check</td>
                  <td style={cellStyle}>{addressCheck?.remarks || (addressCheck ? "Address details verified" : "N/A")}</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Education Verification</td>
                  <td style={{ ...cellStyle, textAlign: "center" }}><StatusBadge status={educationCheck?.length > 0 ? "Verified" : "N/A"} /></td>
                  <td style={cellStyle}>Degree, Roll No Metrics</td>
                  <td style={cellStyle}>{(educationCheck && educationCheck[0]?.remarks) || (educationCheck?.length > 0 ? "Academic details verified" : "N/A")}</td>
                </tr>
                {hasEmployment && (
                  <tr>
                    <td style={cellStyle}>Employment Verification</td>
                    <td style={{ ...cellStyle, textAlign: "center" }}><StatusBadge status="Verified" /></td>
                    <td style={cellStyle}>Experience History Timeline</td>
                    <td style={cellStyle}>{(employmentCheck && employmentCheck[0]?.remarks) || "Work experience verified"}</td>
                  </tr>
                )}
                <tr>
                  <td style={cellStyle}>Criminal Check</td>
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    <StatusBadge status={highRiskCount > 0 ? "Flagged" : "Verified"} />
                  </td>
                  <td style={cellStyle}>Court Records History Match</td>
                  <td style={cellStyle}>
                    {validCriminalRequests.length > 0
                      ? `${validCriminalRequests.length} Case Record(s) Found`
                      : "Clear background check"}
                  </td>
                </tr>
                {hasUanCheck && (
                  <tr>
                    <td style={cellStyle}>UAN / PF Verification</td>
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      <StatusBadge status={uanStatusValue} />
                    </td>
                    <td style={cellStyle}>UAN: {uanNumberValue}</td>
                     <td style={cellStyle}>
                      {rawUanObj?.remarks || rawUanObj?.comment || "Verified against EPFO records"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Personal Report */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <User size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Personal Report</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>
          <div style={{ padding: "0 32px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
              <tbody>
                <tr>
                  <td style={labelCellStyle}>First Name</td>
                  <td style={cellStyle}>{personalCheck?.firstName || "N/A"}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Last Name</td>
                  <td style={cellStyle}>{personalCheck?.lastName || "N/A"}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Date Of Birth</td>
                  <td style={cellStyle}>{personalCheck?.dob || "N/A"}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Father's Name</td>
                  <td style={cellStyle}>{fatherNameValue}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Email Address</td>
                  <td style={cellStyle}>{personalCheck?.email || "N/A"}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Mobile Number</td>
                  <td style={cellStyle}>{mobileNumberValue}</td>
                </tr>
                <tr>
                  <td style={labelCellStyle}>Remarks</td>
                  <td style={cellStyle}>{personalCheck?.remarks || "N/A"}</td>
                </tr>
                <tr>
                  <td style={{ ...labelCellStyle, fontWeight: "700" }}>Verification Status</td>
                  <td style={cellStyle}><StatusBadge status={personalStatusValue} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 4: Address Report */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <MapPin size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Address Report</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>

          <div style={{ padding: "0 36px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
              <thead>
                <tr>
                  <th style={{ ...tableHeaderStyle, width: "24%" }}>Address Type</th>
                  <th style={{ ...tableHeaderStyle, width: "42%" }}>Full Address</th>
                  <th style={{ ...tableHeaderStyle, width: "18%" }}>Proof Submitted</th>
                  <th style={{ ...tableHeaderStyle, width: "16%", textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (!addressCheck) {
                    return (
                      <tr>
                        <td colSpan="4" style={{ ...cellStyle, textAlign: "center", color: "#64748b" }}>
                          No Address Data Submitted
                        </td>
                      </tr>
                    );
                  }

                  const currentFull = [
                    addressCheck.currentAddressLine1,
                    addressCheck.currentAddressLine2,
                    addressCheck.currentCity,
                    addressCheck.currentState,
                    addressCheck.currentCountry,
                    addressCheck.currentPincode || addressCheck.currentZipCode
                  ]
                    .filter((part) => Boolean(part) && String(part).toLowerCase() !== "null")
                    .join(", ");

                  const permFull = [
                    addressCheck.permanentAddressLine1 || addressCheck.permAddressLine1,
                    addressCheck.permanentAddressLine2 || addressCheck.permAddressLine2,
                    addressCheck.permanentCity || addressCheck.permCity,
                    addressCheck.permanentState || addressCheck.permState,
                    addressCheck.permanentCountry || addressCheck.permCountry,
                    addressCheck.permanentPincode || addressCheck.permPincode || addressCheck.permanentZipCode
                  ]
                    .filter((part) => Boolean(part) && String(part).toLowerCase() !== "null")
                    .join(", ");

                  const isSame =
                    permFull.toLowerCase().replace(/[\s,]/g, "") === "" ||
                    currentFull.toLowerCase().replace(/[\s,]/g, "") === permFull.toLowerCase().replace(/[\s,]/g, "") ||
                    addressCheck.isSameAsCurrent === true ||
                    addressCheck.isSameAddress === true;

                  if (isSame) {
                    return (
                      <tr>
                        <td style={{ ...cellStyle, fontWeight: "600" }}>Current & Permanent Address</td>
                        <td style={{ ...cellStyle, color: "#2e7d32" }}>{currentFull || "Address Provided"}</td>
                        <td style={cellStyle}>{addressCheck.proofSubmitted || "Address Proof"}</td>
                        <td style={{ ...cellStyle, textAlign: "center" }}>
                          <StatusBadge status={addressCheck.verificationStatus || "Verified"} />
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <>
                      <tr>
                        <td style={{ ...cellStyle, fontWeight: "600" }}>Current Address</td>
                        <td style={{ ...cellStyle, color: "#2e7d32" }}>{currentFull || "N/A"}</td>
                        <td style={cellStyle}>{addressCheck.currentProofSubmitted || addressCheck.proofSubmitted || "Current Address Proof"}</td>
                        <td style={{ ...cellStyle, textAlign: "center" }}>
                          <StatusBadge status={addressCheck.currentAddressStatus || addressCheck.verificationStatus || "Verified"} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ ...cellStyle, fontWeight: "600" }}>Permanent Address</td>
                        <td style={{ ...cellStyle, color: "#2e7d32" }}>{permFull || "N/A"}</td>
                        <td style={cellStyle}>{addressCheck.permanentProofSubmitted || addressCheck.permanentAddressProofType || "Permanent Address Proof"}</td>
                        <td style={{ ...cellStyle, textAlign: "center" }}>
                          <StatusBadge status={addressCheck.permanentAddressStatus || addressCheck.verificationStatus || "Verified"} />
                        </td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 5: Education Report */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <GraduationCap size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Education Report</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>
          <div style={{ padding: "0 32px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ ...tableHeaderStyle, width: "20%" }}>Qualification</th>
                  <th style={{ ...tableHeaderStyle, width: "30%" }}>Institute</th>
                  <th style={{ ...tableHeaderStyle, width: "12%", textAlign: "center" }}>Enrolled</th>
                  <th style={{ ...tableHeaderStyle, width: "12%", textAlign: "center" }}>Passing</th>
                  <th style={{ ...tableHeaderStyle, width: "13%", textAlign: "center" }}>Status</th>
                  <th style={{ ...tableHeaderStyle, width: "13%" }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {educationCheck && educationCheck.length > 0 ? (
                  educationCheck.map((edu, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                      <td style={cellStyle}>{edu.educationLevel || edu.education_level || edu.qualification || "N/A"}</td>
                      <td style={{ ...cellStyle, color: "#2e7d32" }}>{edu.instituteName || edu.institute_name || "N/A"}</td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>{edu.enrolledYear || edu.enrolled_year || "N/A"}</td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>{edu.passingYear || edu.passing_year || "N/A"}</td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>
                        <StatusBadge status={edu.verificationStatus || edu.education_status || "Verified"} />
                      </td>
                      <td style={cellStyle}>{edu.remarks || "N/A"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ ...cellStyle, textAlign: "center" }}>No Education Records Found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 6: Employment Report */}
        {hasEmployment && (
          <div className="pdf-avoid-break">
            <div style={sectionHeaderStyle}>
              <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
              <Briefcase size={15} color="#001144" />
              <span style={pdfSectionTitleStyle}>Employment Report</span>
              <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
            </div>
            <div style={{ padding: "0 32px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1", tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <th style={{ ...tableHeaderStyle, width: "65px", textAlign: "center" }}>Mode</th>
                    <th style={{ ...tableHeaderStyle, width: "100px" }}>Company</th>
                    <th style={{ ...tableHeaderStyle, width: "80px" }}>Job Title</th>
                    <th style={{ ...tableHeaderStyle, width: "60px" }}>Location</th>
                    <th style={{ ...tableHeaderStyle, width: "105px" }}>Verifier / HR</th>
                    <th style={{ ...tableHeaderStyle, width: "55px", textAlign: "center" }}>From</th>
                    <th style={{ ...tableHeaderStyle, width: "55px", textAlign: "center" }}>To</th>
                    <th style={{ ...tableHeaderStyle, width: "65px" }}>Remarks</th>
                    <th style={{ ...tableHeaderStyle, width: "85px", textAlign: "center" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {employmentCheck && employmentCheck.length > 0 ? (
                    employmentCheck.map((emp, idx) => {
                      const fileUrl = emp.employmentFileUrl || emp.employmentProofFileName || "";
                      const isVirtual =
                        emp.checkMode === "VIRTUAL" ||
                        (emp.check_mode && String(emp.check_mode).toUpperCase() === "VIRTUAL") ||
                        fileUrl.includes("/virtual-employment/");

                      const mode = isVirtual ? "VIRTUAL" : "PHYSICAL";
                      const companyName = emp.companyName || emp.company || emp.CompanyName || "N/A";
                      const jobTitle = emp.jobTitle || emp.designation || emp.Designation || "N/A";
                      const location = emp.location || emp.city || emp.Location || "N/A";

                      const hrEmail = emp.hrContactEmail || emp.verifierEmail || emp.email || "";
                      const hrPhone = emp.hrContactNumber || emp.verifierContactNumber || emp.employerMobileNumber || "";
                      const verifierName = emp.verifierName || "";

                      let contactDetails = [];
                      if (verifierName && verifierName !== "N/A") contactDetails.push(verifierName);
                      if (hrEmail) contactDetails.push(hrEmail);
                      if (hrPhone) contactDetails.push(hrPhone);

                      const workingFrom = emp.workingFrom || emp.durationFrom || emp.duration_from || emp.fromDate || emp.startDate || "N/A";
                      const workingTo = emp.workingTo || emp.durationTo || emp.duration_to || emp.toDate || emp.endDate || "N/A";
                      const status = emp.individualStatus || emp.individual_status || emp.status || emp.verificationStatus || "Verified";

                      return (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                          <td style={{ ...cellStyle, textAlign: "center", padding: "6px 2px" }}>
                            <span
                              style={{
                                fontSize: "8.5px",
                                padding: "2px 5px",
                                borderRadius: "3px",
                                fontWeight: "800",
                                display: "inline-block",
                                background: mode === "VIRTUAL" ? "#eff6ff" : "#f0fdf4",
                                color: mode === "VIRTUAL" ? "#1d4ed8" : "#15803d",
                                border: `1px solid ${mode === "VIRTUAL" ? "#bfdbfe" : "#bbf7d0"}`,
                                whiteSpace: "nowrap"
                              }}
                            >
                              {mode}
                            </span>
                          </td>
                          <td style={{ ...cellStyle, fontWeight: "600" }}>
                            {companyName}
                            {(emp.employeeCode || emp.empCode || emp.employee_code) && (
                              <div style={{ fontSize: "9px", color: "#64748b" }}>
                                ID: {emp.employeeCode || emp.empCode || emp.employee_code}
                              </div>
                            )}
                          </td>
                          <td style={cellStyle}>{jobTitle}</td>
                          <td style={cellStyle}>{location}</td>
                          <td style={{ ...cellStyle, fontSize: "9px" }}>
                            {contactDetails.length > 0 ? contactDetails.join(" / ") : "N/A"}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center" }}>{workingFrom}</td>
                          <td style={{ ...cellStyle, textAlign: "center" }}>{workingTo}</td>
                          <td style={cellStyle}>
                            {emp.remarks || (emp.reasonForLeaving ? `Reason: ${emp.reasonForLeaving}` : "Verified")}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center" }}>
                            <StatusBadge status={status} />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ ...cellStyle, textAlign: "center", color: "#64748b" }}>
                        No employment records found for this candidate.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Section 6B: Dedicated UAN / PF Verification Report Table */}
        {hasUanCheck && (
          <div className="pdf-avoid-break">
            <div style={sectionHeaderStyle}>
              <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
              <Briefcase size={15} color="#001144" />
              <span style={pdfSectionTitleStyle}>UAN / PF Verification Report</span>
              <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
            </div>
            <div style={{ padding: "0 32px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Field</th>
                    <th style={tableHeaderStyle}>Verification Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={labelCellStyle}>UAN Number</td>
                    <td style={valueCellStyle}>{uanNumberValue}</td>
                  </tr>
                  <tr>
                    <td style={labelCellStyle}>PF Member ID</td>
                    <td style={valueCellStyle}>{uanMemberIdValue}</td>
                  </tr>
                  <tr>
                    <td style={labelCellStyle}>Company Name</td>
                    <td style={valueCellStyle}>{uanCompanyNameValue}</td>
                  </tr>
                 
                  <tr>
                    <td style={{ ...labelCellStyle, fontWeight: "700" }}>Verification Status</td>
                    <td style={valueCellStyle}>
                      <StatusBadge status={uanStatusValue} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Section 7: Criminal Report (All Surepass/ecourt Cases) */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <ShieldAlert size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Criminal Report</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>
          <div style={{ padding: "0 32px" }}>
            {validCriminalRequests.length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
                <thead>
                  <tr>
                    <th style={{ ...tableHeaderStyle, width: "10%" }}>Year</th>
                    <th style={{ ...tableHeaderStyle, width: "22%" }}>Petitioner</th>
                    <th style={{ ...tableHeaderStyle, width: "22%" }}>Respondent</th>
                    <th style={{ ...tableHeaderStyle, width: "16%" }}>Court / State</th>
                    <th style={{ ...tableHeaderStyle, width: "16%" }}>Case No / Name</th>
                    <th style={{ ...tableHeaderStyle, width: "14%", textAlign: "center" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {validCriminalRequests.map((cri, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                      <td style={cellStyle}>{cri.year || "N/A"}</td>
                      <td style={cellStyle}>{cri.petitioner || "N/A"}</td>
                      <td style={cellStyle}>{cri.respondent || "N/A"}</td>
                      <td style={cellStyle}>
                        {cri.courtName ? `${cri.courtName}, ` : ""}{cri.state || "N/A"}
                      </td>
                      <td style={cellStyle}>
                        <strong>{cri.caseName || cri.caseNumber || cri.caseNo || "N/A"}</strong>
                        {cri.caseType && <div style={{ fontSize: "8.5px", color: "#64748b" }}>{cri.caseType}</div>}
                      </td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>
                        <StatusBadge status={cri.caseStatus || "Disposed"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div
                style={{
                  border: "1px solid #bbf7d0",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  background: "#f0fdf4",
                  color: "#16a34a",
                  fontSize: "10.5px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <span>✓</span> No Criminal records found - Candidate has a clear criminal history.
              </div>
            )}
          </div>
        </div>

        {/* Section 8: Risk Analysis Matrix */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <FileCheck size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Risk Analysis Matrix</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>

          <div style={{ padding: "0 32px", display: "flex", gap: "10px", width: "100%", boxSizing: "border-box" }}>
            <div style={{ flex: 1, border: "1px solid #fca5a5", background: "#fef2f2", padding: "8px", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#dc2626" }}>{highRiskCount}</div>
              <div style={{ fontSize: "10px", fontWeight: "700", color: "#991b1b" }}>High Risk Findings</div>
            </div>

            <div style={{ flex: 1, border: "1px solid #fde047", background: "#fefce8", padding: "8px", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#ca8a04" }}>{mediumRiskCount}</div>
              <div style={{ fontSize: "10px", fontWeight: "700", color: "#854d0e" }}>Medium Risk Findings</div>
            </div>

            <div style={{ flex: 1, border: "1px solid #bafd93", background: "#effff0", padding: "8px", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#20b420" }}>{lowRiskCount}</div>
              <div style={{ fontSize: "10px", fontWeight: "700", color: "#1eaf5f" }}>Low Risk Findings</div>
            </div>
          </div>
        </div>

        {/* Section 9: Document Annexures */}
        <div>
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <FolderOpen size={15} color="#001144" />
            <span style={pdfSectionTitleStyle}>Document Annexures</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "4px" }} />
          </div>

          <div style={{ padding: "4px 32px 0 32px", fontFamily: "Arial, sans-serif" }}>
            {hasAnyDocuments ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {/* Address Proofs */}
                {hasAddressDocs && (
                  <div>
                    <div style={documentsLabelHeaderStyle}>Address Verification Identity Proofs</div>
                    {currentAddressProofs.map((file, i) => (
                      <div key={`curr-${i}`} className="proof-page-block">
                        <p style={{ fontSize: "10px", fontWeight: "600", margin: "0 0 2px 0", color: "#475569" }}>
                          {permanentAddressProofs.length > 0 ? "Current Address Asset:" : "Address Verification Asset:"}
                        </p>
                        <ReportDocPreview fileUrl={file.url} fileName={file.fileName} />
                      </div>
                    ))}
                    {permanentAddressProofs.map((file, i) => (
                      <div key={`perm-${i}`} className="proof-page-block">
                        <p style={{ fontSize: "10px", fontWeight: "600", margin: "0 0 2px 0", color: "#475569" }}>
                          Permanent Address Asset:
                        </p>
                        <ReportDocPreview fileUrl={file.url} fileName={file.fileName} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Academic Qualifications Proofs */}
                {hasEduDocs && (
                  <div>
                    <div style={documentsLabelHeaderStyle}>Academic Qualifications Proofs</div>
                    {Object.entries(educationProofs).map(([level, files]) =>
                      files.map((file, idx) => (
                        <div key={`${level}-${idx}`} className="proof-page-block">
                          <p style={{ fontSize: "10.5px", fontWeight: "700", margin: "0 0 2px 0", color: "#475569" }}>
                            {level.replace(/_/g, " ")}
                          </p>
                          <ReportDocPreview fileUrl={file.url} fileName={file.fileName} />
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Physical Employment Proofs */}
                {hasEmpDocs && (
                  <div>
                    <div style={documentsLabelHeaderStyle}>Work Experience Certificates (Physical Copy)</div>
                    {Object.entries(employmentPhysicalProofs).map(([company, files]) =>
                      files.map((file, idx) => (
                        <div key={`phys-${company}-${idx}`} className="proof-page-block">
                          <p style={{ fontSize: "10px", fontWeight: "700", margin: "0 0 2px 0", color: "#475569" }}>
                            {company}
                          </p>
                          <ReportDocPreview fileUrl={file.url} fileName={file.fileName} />
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Virtual Employment Proofs */}
                {hasVirtDocs && (
                  <div>
                    <div style={documentsLabelHeaderStyle}>Work Experience Proofs & Screenshot Evidence (Virtual Check)</div>
                    {Object.entries(employmentVirtualProofs).map(([company, files]) =>
                      files.map((file, idx) => (
                        <div key={`virt-${company}-${idx}`} className="proof-page-block">
                          <p style={{ fontSize: "10px", fontWeight: "700", margin: "0 0 2px 0", color: "#475569" }}>
                            {company} {file.employeeCode ? `(Emp Code: ${file.employeeCode})` : ""}
                          </p>
                          <ReportDocPreview fileUrl={file.url} fileName={file.fileName} />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: "11px",
                  fontStyle: "italic",
                  border: "2px dashed #cbd5e1",
                  borderRadius: "6px",
                  background: "#f8fafc"
                }}
              >
                No active document proofs were rendered.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={liveUiFooterStyle}>
          <div>TrueFace</div>
          <span>Powered by Clientserver Solutions PVT LTD</span>
          <span>hrind@clientservertech.com</span>
        </div>
      </div>
    </div>
  );
};

export default FinalReportPage;